'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  ArrowRight,
  Loader2,
  Copy,
  Calendar,
  Video,
  Sparkles,
  Link2,
  DollarSign,
  Rocket,
} from 'lucide-react';
import { createBrowserClient } from '@/lib/supabase-clients';
import { AvatarUpload } from '@/components/settings/AvatarUpload';
import AvailabilityEditor from '@/components/settings/AvailabilityEditor';
import BookingTypeEditor from '@/components/settings/BookingTypeEditor';
import type { BookingType } from '@/lib/services/guide-profile-service';
import type { AvailabilitySlot } from '@/lib/services/guide-availability-service';
import { openRecalOAuth, RecalOAuthError } from '@/lib/utils/open-recal-oauth';
import { OUTLOOK_ENABLED } from '@/lib/constants/integrations';
import { toast } from 'sonner';

interface GuideStartPageProps {
  authenticated: boolean;
  userId?: string;
  userName?: string;
  userHandle?: string;
  userEmail?: string;
  avatarUrl?: string | null;
  guideProfile?: any;
}

const DEFAULT_BOOKING_TYPES: BookingType[] = [
  { id: '15min', label: 'Intro Chat', duration_minutes: 15, price_cents: 0, currency: 'usd', is_free: true },
  { id: '30min', label: 'Focused Session', duration_minutes: 30, price_cents: 7500, currency: 'usd', is_free: false },
  { id: '60min', label: 'Deep Dive', duration_minutes: 60, price_cents: 15000, currency: 'usd', is_free: false },
];

const DEFAULT_AVAILABILITY: AvailabilitySlot[] = [
  { day_of_week: 1, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 2, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 3, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 4, start_time: '09:00', end_time: '17:00' },
  { day_of_week: 5, start_time: '09:00', end_time: '17:00' },
];

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

const BENEFITS = [
  { icon: Link2, text: 'Your own booking page at apprendo.co/@you' },
  { icon: Video, text: 'HD video calls with recording & transcription' },
  { icon: Sparkles, text: 'AI-extracted moments from every conversation' },
  { icon: Calendar, text: 'Calendar sync — no double-bookings' },
  { icon: DollarSign, text: 'Charge for your time with built-in Stripe payments' },
];

export default function GuideStartPage({
  authenticated,
  userId,
  userName,
  userHandle,
  userEmail,
  avatarUrl,
  guideProfile,
}: GuideStartPageProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const calendarConnected = searchParams.get('calendar') === 'connected' || searchParams.get('success') === 'calendar_connected';

  if (!authenticated) {
    return <PublicView />;
  }

  if (guideProfile?.is_active) {
    return <SuccessView handle={userHandle || ''} />;
  }

  return (
    <SetupView
      userId={userId!}
      userName={userName || ''}
      userHandle={userHandle || ''}
      userEmail={userEmail || ''}
      avatarUrl={avatarUrl || null}
      calendarConnected={calendarConnected}
      existingProfile={guideProfile}
    />
  );
}

function PublicView() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);

    try {
      const supabase = createBrowserClient();
      const { error: authError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/api/auth/callback?next=/start`,
          shouldCreateUser: true,
        },
      });

      if (authError) {
        setError(authError.message);
      } else {
        setStep('otp');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) return;
    setLoading(true);
    setError(null);

    try {
      const supabase = createBrowserClient();
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (verifyError) {
        setError(verifyError.message);
      } else {
        window.location.reload();
      }
    } catch {
      setError('Invalid code. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-indigo-950/10 dark:to-purple-950/10">
      <div className="max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-3">
            Your meetings are worth more than you think.
          </h1>
          <p className="text-muted-foreground">
            One link for booking, video calls, and AI-powered highlights from every conversation. Your best ideas shouldn&apos;t disappear when the call ends.
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6">
            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="start-email">Your email</Label>
                  <Input
                    id="start-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    spellCheck="false"
                    translate="no"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Get Your Free Page
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  We sent a code to <strong>{email}</strong>
                </p>
                <div>
                  <Label htmlFor="start-otp">Verification code</Label>
                  <Input
                    id="start-otp"
                    type="text"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    className="mt-1 text-center text-lg tracking-widest"
                    maxLength={6}
                    spellCheck="false"
                    translate="no"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verify
                </Button>
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground w-full text-center"
                  onClick={() => { setStep('email'); setOtp(''); setError(null); }}
                >
                  Use a different email
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.text} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span>{b.text}</span>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Everything Calendly + Zoom do, plus AI that turns your calls into content. Free to start.
        </p>
      </div>
    </div>
  );
}

function SetupView({
  userId,
  userName,
  userHandle,
  userEmail,
  avatarUrl,
  calendarConnected,
  existingProfile,
}: {
  userId: string;
  userName: string;
  userHandle: string;
  userEmail: string;
  avatarUrl: string | null;
  calendarConnected: boolean;
  existingProfile: any;
}) {
  const router = useRouter();
  const [name, setName] = useState(userName);
  const [headline, setHeadline] = useState(existingProfile?.headline || '');
  const [avatar, setAvatar] = useState(avatarUrl);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>(
    existingProfile?.booking_types || DEFAULT_BOOKING_TYPES
  );
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>(DEFAULT_AVAILABILITY);
  const [timezone, setTimezone] = useState(detectTimezone());
  const [isLaunching, setIsLaunching] = useState(false);
  const [launched, setLaunched] = useState(false);
  // Tracks an in-session popup-OAuth success so the UI updates without a
  // page reload. Combined with the prop-derived `calendarConnected` (from
  // URL query params) so legacy redirect flow still works.
  const [localCalendarConnected, setLocalCalendarConnected] = useState(false);
  const isCalendarConnected = calendarConnected || localCalendarConnected;

  const handleConnectCalendar = async (provider: 'google' | 'microsoft') => {
    try {
      const result = await openRecalOAuth(provider, { returnUrl: '/start' });
      if (result.status === 'connected') {
        setLocalCalendarConnected(true);
        toast.success('Calendar connected');
      } else {
        toast.error(`Calendar connection failed${result.error ? `: ${result.error}` : ''}`);
      }
    } catch (err) {
      if (err instanceof RecalOAuthError) {
        if (err.code === 'POPUP_BLOCKED') {
          toast.error('Allow popups for apprendo.co to connect your calendar.');
        } else if (err.code === 'POPUP_CLOSED') {
          // User cancelled — no toast needed.
        } else {
          toast.error('Calendar connection failed.');
        }
      } else {
        toast.error('Calendar connection failed.');
      }
    }
  };

  const handleBookingTypeChange = (index: number, updated: BookingType) => {
    const types = [...bookingTypes];
    types[index] = updated;
    setBookingTypes(types);
  };

  const handleRemoveBookingType = (index: number) => {
    setBookingTypes(bookingTypes.filter((_, i) => i !== index));
  };

  const handleAddBookingType = () => {
    setBookingTypes([
      ...bookingTypes,
      { id: `custom_${Date.now()}`, label: 'Custom Session', duration_minutes: 30, price_cents: 5000, currency: 'usd', is_free: false },
    ]);
  };

  const handleLaunch = async () => {
    if (!headline.trim()) {
      document.getElementById('headline-input')?.focus();
      return;
    }
    if (availabilitySlots.length === 0) return;

    setIsLaunching(true);
    try {
      const res = await fetch('/api/guide/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          headline: headline.trim(),
          booking_types: bookingTypes,
          availability_slots: availabilitySlots,
          timezone,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to launch');
      }

      setLaunched(true);
    } catch (error) {
      console.error('[GuideStartPage] Launch error:', error);
    }
    setIsLaunching(false);
  };

  if (launched) {
    return <SuccessView handle={userHandle} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-indigo-950/10 dark:to-purple-950/10">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-2">You&apos;re in. Let&apos;s set up your page.</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Every conversation you take from here gets recorded, transcribed, and scanned for moments worth sharing. First, let&apos;s get you bookable.
          </p>
        </div>

        <div className="space-y-8">
          {/* Section 1: Profile */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4">Your Profile</h2>
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-shrink-0">
                  <AvatarUpload
                    userId={userId}
                    currentAvatarUrl={avatar}
                    userName={name}
                    onAvatarUpdate={(url) => setAvatar(url)}
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <Label htmlFor="name-input">Name</Label>
                    <Input
                      id="name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="headline-input">Headline *</Label>
                    <Input
                      id="headline-input"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder="Marketing Coach, Solar Consultant, etc."
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">One line about what you do</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Availability */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-1">Your Hours</h2>
              <p className="text-sm text-muted-foreground mb-4">Select the hours when people can book time with you. These are your recurring weekly windows. You can adjust them anytime in settings.</p>
              <AvailabilityEditor
                slots={availabilitySlots}
                timezone={timezone}
                onChange={(slots, tz) => {
                  setAvailabilitySlots(slots);
                  setTimezone(tz);
                }}
              />
            </CardContent>
          </Card>

          {/* Section 3: Calendar — placed right after Hours so the
              double-booking explanation reads as a direct extension of the
              availability the user just configured. */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-1">Connect Calendar</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Connect your Google calendar to automatically prevent double-bookings — if you have a meeting during your available hours, that time slot won&apos;t be shown to people booking you. You can also do this later in settings.
              </p>
              {isCalendarConnected ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800">
                    <Check className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                  <span className="text-sm text-muted-foreground">Your calendar is synced</span>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => handleConnectCalendar('google')}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Connect Google Calendar
                  </Button>
                  {OUTLOOK_ENABLED && (
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => handleConnectCalendar('microsoft')}
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#00a4ef" d="M1 12h10v10H1z"/><path fill="#7fba00" d="M12 1h10v10H12z"/><path fill="#ffb900" d="M12 12h10v10H12z"/></svg>
                      Connect Outlook
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section 4: Sessions */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-1">Your Sessions</h2>
              <p className="text-sm text-muted-foreground mb-4">What sessions do you offer? Adjust prices or keep the defaults.</p>
              <div className="space-y-3">
                {bookingTypes.map((bt, i) => (
                  <BookingTypeEditor
                    key={bt.id}
                    bookingType={bt}
                    onChange={(updated) => handleBookingTypeChange(i, updated)}
                    onRemove={() => handleRemoveBookingType(i)}
                    canRemove={bookingTypes.length > 1}
                  />
                ))}
                <Button variant="outline" size="sm" onClick={handleAddBookingType}>
                  + Add session type
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview + Launch */}
          <Card className="border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-lg font-bold text-indigo-600">
                  {name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <div className="font-semibold">{name || 'Your Name'}</div>
                  <div className="text-sm text-muted-foreground">{headline || 'Your headline'}</div>
                  <div className="text-xs text-muted-foreground">apprendo.co/@{userHandle}</div>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-lg py-6"
                onClick={handleLaunch}
                disabled={isLaunching || !headline.trim() || availabilitySlots.length === 0}
              >
                {isLaunching ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Rocket className="w-5 h-5 mr-2" />
                )}
                Launch My Booking Page
              </Button>

              {!headline.trim() && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 text-center">Add a headline to launch</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SuccessView({ handle }: { handle: string }) {
  const [copied, setCopied] = useState(false);
  const bookingUrl = `apprendo.co/@${handle}`;
  const fullUrl = `https://${bookingUrl}`;

  const copyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 via-green-50/30 to-indigo-50/30 dark:from-gray-950 dark:via-green-950/10 dark:to-indigo-950/10">
      <div className="max-w-md w-full mx-auto text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-green-600" />
        </div>

        <h1 className="text-3xl font-bold mb-2">You&apos;re live!</h1>
        <p className="text-muted-foreground mb-8">Your booking page is ready. Share it with anyone.</p>

        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
              <span className="flex-1 text-sm font-mono truncate">{bookingUrl}</span>
              <Button size="sm" variant="ghost" onClick={copyLink}>
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <Button variant="outline" size="sm" onClick={copyLink}>
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Book a conversation with me: ${fullUrl}`)}`, '_blank')}
              >
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`, '_blank')}
              >
                LinkedIn
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`mailto:?subject=${encodeURIComponent('Book a conversation with me')}&body=${encodeURIComponent(`Here's my booking page: ${fullUrl}`)}`, '_blank')}
              >
                Email
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3 text-left mb-8">
          <p className="text-sm font-medium">What happens next:</p>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> People visit your link and pick a time</div>
            <div className="flex gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> You meet in an HD video room</div>
            <div className="flex gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> AI extracts moments and creates content</div>
            <div className="flex gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Everything is searchable and reusable</div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => window.location.href = '/dashboard'}>
            Go to Dashboard
          </Button>
          <Button className="flex-1" onClick={() => window.location.href = `/@${handle}`}>
            View My Page
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
